import { CommonModule, DatePipe } from '@angular/common';
import { Component } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Client, PageInfo, PermissionsEnum, PrepaymentDataOut, PrepaymentGetAllDataIn, SortingType, DownloadPrepaymentDataIn } from '../../../api/api-reference';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../../services/auth.service';
import { DateRangeInput } from '../../core/models/date-range-input';
import { SortComponent } from '../../core/components/sort/sort.component';
import { DatepickerComponent } from '../../core/components/datepicker/datepicker.component';
import { SearchComponent } from '../../core/components/search/search.component';
import { PaginationComponent } from '../../core/components/pagination/pagination.component';
import { UnitConverter } from '../../../pipes/unit-conversion';
import { IsNewDirective } from '../../../directives/is-new.directive';
import { ClipboardService } from '../../../services/clipboard.service';
import { FileService } from "../../../services/file.service";
import { processDateRange } from '../../../helper/date-utils';
import { generateFileNameAppendix } from '../../../helper/download-filename';

@Component({
  selector: 'app-pre-payment-report',
  standalone: true,
  imports: [
    TranslateModule,
    SortComponent,
    DatepickerComponent,
    SearchComponent,
    PaginationComponent,
    DatePipe,
    CommonModule,
    UnitConverter,
    IsNewDirective,
  ],
  templateUrl: './pre-payment-report.component.html',
  styleUrl: './pre-payment-report.component.scss'
})
export class PrePaymentReportComponent {
  public pageSize: number = 10;
  public pageNumber: number = 1;
  public currentPage: number = 1;
  public numberOfPrepayment: number = 0;
  public lastPageSize: number = 1;
  public PermissionsEnum = PermissionsEnum;
  data: PrepaymentDataOut[] | undefined = [];

  readonly dayInMs = 24 * 60 * 60 * 1000;
  isDatepickerOpen = false;

  requestData = new PrepaymentGetAllDataIn({
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.pageNumber }),
    search: undefined,
    sorting: SortingType.Descending,
    startDate: undefined,
    endDate: undefined,
  });

  constructor(
    private client: Client,
    private toastr: ToastrService,
    public authService: AuthService,
    public clipboardService: ClipboardService,
    private fileService: FileService
  ) {
    this.reloadTable(1);
  }


  reloadTable(page?: number) {
    this.pageNumber = page || 0;
    if (page) this.requestData!.pageInfo!.page = this.pageNumber;

    this.client.queryPrepaymentReport(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data;
        this.numberOfPrepayment = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfPrepayment / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  clearDates(): void {
    this.requestData.startDate = undefined;
    this.requestData.endDate = undefined;
    this.reloadTable();
  }

  searchChange(searchText: string) {
    this.requestData.search = searchText;
    this.reloadTable(1);
  }

  pageChange(value: number) {
    this.currentPage = value;
    this.reloadTable(this.currentPage);
  }

  onSortOption(selectedSort?: SortingType) {
    if (selectedSort) this.requestData.sorting = selectedSort;
    this.reloadTable();
  }

  ensureDate(dateTime: unknown): Date | null {
    return dateTime instanceof Date ? dateTime : null;
  }

  selectedDate: string | null = '';
  onDateRangeChanged(input: DateRangeInput): void {
    const result = processDateRange(input);

    this.requestData.startDate = result.startDate;
    this.requestData.endDate = result.endDate;
    this.selectedDate = result.selectedDate;
    this.filterDatesInTable();
  }

  toggleModal() {
    this.isDatepickerOpen = !this.isDatepickerOpen;
  }

  filterDatesInTable() {
    this.client.queryPrepaymentReport(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data;
        this.numberOfPrepayment = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfPrepayment / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  download() {
    const fileNameApendix = generateFileNameAppendix();

    const data = new DownloadPrepaymentDataIn({
      search: this.requestData.search,
      sorting: this.requestData.sorting,
      startDate: this.requestData.startDate,
      endDate: this.requestData.endDate
    });

    this.client.downloadPrepaymentReportFile(data).subscribe({
      next: (response) => {
        this.fileService.downloadFile(
          response.data,
          response.fileName || `${'Prepayment'}${fileNameApendix}`,
        );
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }
}
